/**
 * Site-wide search and share metadata for defera.app: one definition, read by
 * the live pages (react-helmet, through PageSeo, HelpSeo and SeoDefaults) and
 * by the build-time pre-renderer (scripts/prerender-help.mjs), so the HTML a
 * crawler fetches and the page a visitor sees always agree.
 *
 * Plain JavaScript with relative imports only: the pre-renderer loads it in Node.
 * Structured data states only what the site itself says — no ratings,
 * reviews, prices or certifications.
 */

export const SITE_ORIGIN = 'https://defera.app';
export const SITE_NAME = 'Defera';
export const TITLE_SUFFIX = ' | Defera';
export const LOGO_URL = `${SITE_ORIGIN}/defera-transparent.png`;

/** The 1200×630 image link previews show for every page (public/og-image.png). */
export const SHARE_IMAGE = {
  url: `${SITE_ORIGIN}/og-image.png`,
  width: 1200,
  height: 630,
  alt: 'Defera logo above Book flights in installments: pay over time, no credit check, no interest, over a glowing blue horizon.',
};

/** Title and description for any page that doesn't set its own (and the app shell). */
export const DEFAULT_TITLE = 'Book Flights Now, Pay Later in Installments | Defera';
export const DEFAULT_DESCRIPTION =
  'Book flights now and pay later in weekly or bi-weekly installments, with no credit check and no interest. Lock in today’s price, with $0 down on select fares.';

export const ORGANIZATION = {
  '@type': 'Organization',
  '@id': `${SITE_ORIGIN}/#organization`,
  name: SITE_NAME,
  url: `${SITE_ORIGIN}/`,
  logo: { '@type': 'ImageObject', url: LOGO_URL },
  contactPoint: { '@type': 'ContactPoint', contactType: 'customer support', url: `${SITE_ORIGIN}/contact` },
};

/** "Some title" → "Some title | Defera"; a title that already names Defera ("About Defera") is left alone. */
export function pageTitle(title) {
  if (!title) return DEFAULT_TITLE;
  return /\bDefera\b/.test(title) ? title : `${title}${TITLE_SUFFIX}`;
}

export const absoluteUrl = (path) => (/^https?:\/\//.test(path) ? path : `${SITE_ORIGIN}${path === '/' ? '/' : path}`);

/** Organization + WebSite: the identity every page shares. */
export function siteJsonLd() {
  return [
    { '@context': 'https://schema.org', ...ORGANIZATION },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${SITE_ORIGIN}/#website`,
      name: SITE_NAME,
      url: `${SITE_ORIGIN}/`,
      publisher: { '@id': ORGANIZATION['@id'] },
    },
  ];
}

/** BreadcrumbList from [{ name, path }], Home first. */
export function breadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({ '@type': 'ListItem', position: i + 1, name: it.name, item: absoluteUrl(it.path) })),
  };
}

/** FAQPage from the questions a page SHOWS ([{ q, a }] in plain text). */
export function faqPageJsonLd(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
}

/**
 * A page's metadata from its path, title and description: the canonical URL
 * and every share tag come with it, so no page can end up with a title but no
 * share image (or the reverse).
 */
export function pageMeta({ path, title, description, type = 'website', jsonLd = [], noindex = false, image = SHARE_IMAGE }) {
  return {
    title: pageTitle(title),
    description: description || DEFAULT_DESCRIPTION,
    canonical: absoluteUrl(path),
    type,
    image,
    noindex,
    jsonLd,
  };
}

/**
 * The <head> tags for a page's metadata, as plain data: PageSeo and HelpSeo
 * render them through react-helmet, the pre-renderer writes them as HTML.
 * The <title> is separate (meta.title).
 */
export function headTags(meta) {
  if (!meta) return [];
  const image = meta.image || SHARE_IMAGE;
  return [
    { tag: 'meta', attrs: { name: 'description', content: meta.description } },
    ...(meta.noindex ? [{ tag: 'meta', attrs: { name: 'robots', content: 'noindex' } }] : []),
    ...(meta.canonical ? [{ tag: 'link', attrs: { rel: 'canonical', href: meta.canonical } }] : []),
    { tag: 'meta', attrs: { property: 'og:type', content: meta.type || 'website' } },
    { tag: 'meta', attrs: { property: 'og:site_name', content: SITE_NAME } },
    ...(meta.canonical ? [{ tag: 'meta', attrs: { property: 'og:url', content: meta.canonical } }] : []),
    { tag: 'meta', attrs: { property: 'og:title', content: meta.title } },
    { tag: 'meta', attrs: { property: 'og:description', content: meta.description } },
    { tag: 'meta', attrs: { property: 'og:image', content: image.url } },
    ...(image.width ? [
      { tag: 'meta', attrs: { property: 'og:image:width', content: String(image.width) } },
      { tag: 'meta', attrs: { property: 'og:image:height', content: String(image.height) } },
    ] : []),
    ...(image.alt ? [{ tag: 'meta', attrs: { property: 'og:image:alt', content: image.alt } }] : []),
    { tag: 'meta', attrs: { name: 'twitter:card', content: 'summary_large_image' } },
    { tag: 'meta', attrs: { name: 'twitter:title', content: meta.title } },
    { tag: 'meta', attrs: { name: 'twitter:description', content: meta.description } },
    { tag: 'meta', attrs: { name: 'twitter:image', content: image.url } },
    ...(image.alt ? [{ tag: 'meta', attrs: { name: 'twitter:image:alt', content: image.alt } }] : []),
    ...(meta.jsonLd || []).map((data) => ({ tag: 'script', attrs: { type: 'application/ld+json' }, json: data })),
  ];
}

/** JSON for an inline <script>: `<` escaped so page text can never close the tag. */
export const jsonForScript = (data) => JSON.stringify(data).replace(/</g, '\\u003c');

const escHtml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Markers around the head tags in index.html; the build fills them, the pre-renderer swaps them per page. */
export const SEO_BLOCK_START = '<!-- seo:start -->';
export const SEO_BLOCK_END = '<!-- seo:end -->';
export const SEO_BLOCK_RE = /<!-- seo:start -->[\s\S]*?<!-- seo:end -->/;

/** The app shell's metadata: the site defaults, with no canonical (the shell answers every app URL). */
export function defaultMeta() {
  return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION, image: SHARE_IMAGE, jsonLd: siteJsonLd() };
}

/**
 * A page's <title> and head tags as HTML, between the seo markers. Tags carry
 * data-rh="true" so react-helmet takes them over on start instead of adding
 * duplicates.
 */
export function headBlockHtml(meta, indent = '    ') {
  const tags = headTags(meta).map(({ tag, attrs, json }) => {
    const a = Object.entries(attrs).map(([k, v]) => ` ${k}="${escHtml(v)}"`).join('');
    if (tag === 'script') return `<script${a} data-rh="true">${jsonForScript(json)}</script>`;
    return `<${tag}${a} data-rh="true" />`;
  });
  return [SEO_BLOCK_START, `<title>${escHtml(meta.title)}</title>`, ...tags, SEO_BLOCK_END].join(`\n${indent}`);
}
